// Admin view: audit log + user management

function UsersTab({ user }) {
  const [users, setUsers]         = React.useState([]);
  const [invites, setInvites]     = React.useState([]);
  const [loading, setLoading]     = React.useState(true);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole]   = React.useState('operator');
  const [inviteStatus, setInviteStatus] = React.useState(null); // null | 'loading' | 'ok' | 'error'
  const [inviteErr, setInviteErr]     = React.useState('');
  const [inviteLink, setInviteLink]   = React.useState('');
  const [patchStatus, setPatchStatus] = React.useState({}); // {userId: 'loading'|'ok'|'error'}

  async function load() {
    try {
      const [uRes, iRes] = await Promise.all([fetch('/users'), fetch('/users/invitations')]);
      if (uRes.ok) setUsers(await uRes.json());
      if (iRes.ok) setInvites(await iRes.json());
    } catch (_) {}
    setLoading(false);
  }

  React.useEffect(() => { load(); }, []);

  async function sendInvite(e) {
    e?.preventDefault?.();
    if (!inviteEmail) return;
    setInviteStatus('loading'); setInviteErr('');
    try {
      const res = await fetch('/users/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setInviteStatus('error'); setInviteErr(data.detail || 'Error al enviar invitación.'); return;
      }
      const data = await res.json();
      setInviteLink(data.link || '');
      setInviteStatus('ok'); setInviteEmail('');
      load();
    } catch (_) {
      setInviteStatus('error'); setInviteErr('Error de conexión.');
    }
  }

  async function revokeInvite(id) {
    try {
      await fetch(`/users/invitations/${id}`, { method: 'DELETE' });
      load();
    } catch (_) {}
  }

  async function patchUser(u, changes) {
    setPatchStatus(p => ({ ...p, [u.id]: 'loading' }));
    try {
      const res = await fetch(`/users/${u.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changes),
      });
      setPatchStatus(p => ({ ...p, [u.id]: res.ok ? 'ok' : 'error' }));
      if (res.ok) load();
      setTimeout(() => setPatchStatus(p => { const n={...p}; delete n[u.id]; return n; }), 2000);
    } catch (_) {
      setPatchStatus(p => ({ ...p, [u.id]: 'error' }));
    }
  }

  const toggleActive = (u) => patchUser(u, { active: !u.active });
  // The user's dashboard shows alerts in their simplified wording (as in WhatsApp).
  const toggleSimple = (u) => patchUser(u, { simple_view: !u.simple_view });
  // ... and with the device address after its location.
  const toggleAddresses = (u) => patchUser(u, { include_addresses: !u.include_addresses });

  const isMe = (u) => u.id === user.id;

  const roleColors = { admin: { bg: BC.supervisory.bg, fg: BC.supervisory.fg }, operator: { bg: BC.trouble.bg, fg: BC.trouble.fg } };
  function RoleChip({ role }) {
    const c = roleColors[role] || { bg: BC.bgAlt, fg: BC.inkSubtle };
    return <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:4, background:c.bg, color:c.fg, textTransform:'uppercase', letterSpacing:'.04em' }}>{role}</span>;
  }

  const formatDate = ts => ts ? new Date(ts).toLocaleDateString('es-CL') : '—';

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:18 }}>

      {/* Invite panel */}
      <div style={{ background:BC.surface, border:'1px solid '+BC.border, borderRadius:8, boxShadow:BC.shadow }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid '+BC.border }}>
          <div style={{ fontSize:14, fontWeight:600 }}>Invitar usuario</div>
          <div style={{ fontSize:11, color:BC.inkSubtle, marginTop:2 }}>
            El usuario recibirá un enlace para crear su cuenta. La invitación expira en 72 horas.
          </div>
        </div>
        <form onSubmit={sendInvite} style={{ padding:'14px 18px', display:'flex', gap:8, alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={{ flex:2, minWidth:200 }}>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:BC.inkSubtle, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:5 }}>Correo</label>
            <input className="bc-input" type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="correo@empresa.com"
              style={{ width:'100%', padding:'8px 10px', border:'1px solid '+BC.border, borderRadius:5, background:BC.surfaceAlt, fontSize:13, color:BC.ink }}/>
          </div>
          <div style={{ flex:1, minWidth:120 }}>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:BC.inkSubtle, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:5 }}>Rol</label>
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
              style={{ width:'100%', padding:'8px 10px', border:'1px solid '+BC.border, borderRadius:5, background:BC.surfaceAlt, fontSize:13, color:BC.ink }}>
              <option value="operator">Operador</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          <div>
            <button type="submit" disabled={inviteStatus==='loading'} className="bc-btn"
              style={{ padding:'8px 16px', background:BC.brand, color:'#fff', border:'none', borderRadius:5, fontSize:13, fontWeight:600 }}>
              {inviteStatus==='loading' ? '…' : 'Enviar invitación'}
            </button>
          </div>
        </form>
        {inviteStatus === 'ok' && inviteLink && (
          <div style={{ margin:'0 18px 14px', padding:'12px 14px', background:BC.ok.bg, border:'1px solid '+BC.ok.bar, borderRadius:5 }}>
            <div style={{ fontSize:12, color:BC.ok.fg, display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
              <Icon name="check" size={13}/> Invitación creada. Comparte este enlace con el usuario:
            </div>
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              <input readOnly value={inviteLink}
                style={{ flex:1, padding:'6px 10px', border:'1px solid '+BC.border, borderRadius:4, background:BC.surface, fontSize:11, color:BC.inkMuted, fontFamily:BC.mono }}
                onFocus={e => e.target.select()}/>
              <button className="bc-btn" onClick={() => { navigator.clipboard.writeText(inviteLink); }}
                style={{ padding:'6px 12px', background:BC.brand, color:'#fff', border:'none', borderRadius:4, fontSize:12, fontWeight:600, whiteSpace:'nowrap' }}>
                Copiar
              </button>
            </div>
          </div>
        )}
        {inviteStatus === 'error' && (
          <div style={{ margin:'0 18px 14px', padding:'8px 12px', background:BC.fire.bg, color:BC.fire.fg, border:'1px solid '+BC.fire.bar, borderRadius:5, fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
            <Icon name="warning" size={13}/> {inviteErr}
          </div>
        )}
      </div>

      {/* Pending invitations */}
      {invites.length > 0 && (
        <div style={{ background:BC.surface, border:'1px solid '+BC.border, borderRadius:8, boxShadow:BC.shadow }}>
          <div style={{ padding:'14px 18px', borderBottom:'1px solid '+BC.border, fontSize:13, fontWeight:600 }}>
            Invitaciones pendientes <span style={{ fontFamily:BC.mono, fontSize:11, padding:'1px 6px', borderRadius:4, background:BC.pending.bg, color:BC.pending.fg, marginLeft:6 }}>{invites.length}</span>
          </div>
          <div className="bc-admin-table-wrap"><table className="bc-tbl" style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><th>Correo</th><th>Rol</th><th>Expira</th><th></th></tr></thead>
            <tbody>
              {invites.map(inv => (
                <tr key={inv.id}>
                  <td style={{ fontSize:12, fontFamily:BC.mono }}>{inv.email}</td>
                  <td><RoleChip role={inv.role}/></td>
                  <td style={{ fontSize:11, color:BC.inkSubtle }}>{formatDate(inv.expires_at)}</td>
                  <td>
                    <button onClick={() => revokeInvite(inv.id)} className="bc-btn"
                      style={{ padding:'3px 10px', fontSize:11, background:'transparent', color:BC.fire.fg, border:'1px solid '+BC.fire.bar, borderRadius:4 }}>
                      Revocar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {/* Users table */}
      <div style={{ background:BC.surface, border:'1px solid '+BC.border, borderRadius:8, boxShadow:BC.shadow }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid '+BC.border }}>
          <div style={{ fontSize:14, fontWeight:600 }}>Usuarios</div>
          <div style={{ fontSize:11, color:BC.inkSubtle, marginTop:2 }}>Usuarios activos e inactivos de tu empresa.</div>
        </div>
        {loading ? (
          <div style={{ padding:'40px', textAlign:'center', color:BC.inkFaint, fontSize:12 }}>Cargando…</div>
        ) : (
          <div className="bc-admin-table-wrap"><table className="bc-tbl" style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><th>Usuario</th><th>Correo</th><th>Rol</th><th>Estado</th><th title="Alarmas, fallas y supervisiones como en WhatsApp: qué pasó y dónde">Texto simplificado</th><th title="Con el texto simplificado, la dirección del dispositivo después de su ubicación">Incluir direcciones</th><th></th></tr></thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign:'center', padding:'24px', color:BC.inkFaint }}>Sin usuarios.</td></tr>
              )}
              {users.map(u => (
                <tr key={u.id}>
                  <td style={{ fontWeight:500, fontSize:13 }}>
                    {u.username}
                    {isMe(u) && <span style={{ fontSize:10, color:BC.inkFaint, marginLeft:6 }}>(tú)</span>}
                  </td>
                  <td style={{ fontSize:12, color:BC.inkMuted }}>{u.email || '—'}</td>
                  <td><RoleChip role={u.role}/></td>
                  <td>
                    <span style={{ fontSize:11, display:'inline-flex', alignItems:'center', gap:4, color: u.active ? BC.ok.fg : BC.fire.fg }}>
                      <Icon name={u.active ? 'check' : 'close'} size={12}/>{u.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <input type="checkbox" checked={!!u.simple_view} onChange={() => toggleSimple(u)}
                      disabled={patchStatus[u.id]==='loading'} title="Texto simplificado"
                      style={{ width:16, height:16, cursor:'pointer', accentColor: BC.brand }}/>
                  </td>
                  <td>
                    <input type="checkbox" checked={!!u.include_addresses} onChange={() => toggleAddresses(u)}
                      disabled={!u.simple_view || patchStatus[u.id]==='loading'} title={u.simple_view ? 'Incluir direcciones' : 'Solo con texto simplificado'}
                      style={{ width:16, height:16, cursor: u.simple_view ? 'pointer' : 'default', accentColor: BC.brand, opacity: u.simple_view ? 1 : 0.4 }}/>
                  </td>
                  <td>
                    {!isMe(u) && (
                      <button onClick={() => toggleActive(u)} disabled={patchStatus[u.id]==='loading'} className="bc-btn"
                        style={{ padding:'3px 10px', fontSize:11, background:'transparent', border:'1px solid '+BC.border, borderRadius:4, color:BC.inkMuted }}>
                        {patchStatus[u.id]==='loading' ? '…' : u.active ? 'Desactivar' : 'Activar'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

// ───────── Unresolved events + manual PANEL OK ─────────

const BRAND_LABELS = {
  notifier: 'Notifier', notifier_3030: 'Notifier 3030', simplex: 'Simplex', vigilant: 'Vigilant',
  kidde: 'Kidde', hochiki: 'Hochiki', generic_cid: 'Contact ID', multi_cid: 'Contact ID', mircom: 'Mircom FX-4003',
  'mircom-fx-3500': 'Mircom FX-3500',
};

// How each brand's PANEL OK comes on its own (app/panel_ok.py).
const RECOVERY_RULES = {
  notifier: 'Se emite cuando el panel envía "SISTEMA NORMAL" ("SYSTEM NORMAL" en inglés).',
  simplex: 'Se emite cuando un operador consulta ALARMAS EN CURSO y el panel responde FIRE, PRIORITY2, SUPERVISORY y TROUBLE en 0.',
  vigilant: 'Se emite cuando cada activación (ACT) recibe su restauración (RST).',
  kidde: 'Se emite cuando cada activación (ACT) recibe su restauración (RST).',
  hochiki: 'Se emite con un RESET del panel (RESET CLEARED), o cuando cada falla recibe su CLEARED.',
  notifier_3030: 'Se emite cuando cada punto activo recibe su BORRADO/BORRADA. SYSTEM RESET no cuenta.',
  generic_cid: 'Se emite cuando cada evento recibe su restauración, o con un reset del sistema (códigos 305/313).',
  multi_cid: 'Se emite cuando cada evento recibe su restauración, o con un reset del sistema (códigos 305/313).',
  mircom: 'Se emite cuando cada punto abierto recibe su restauración.',
  'mircom-fx-3500': 'Se emite cuando cada alarma, falla o supervisión recibe su restauración en el mismo punto. Un reset del sistema no cuenta, y la evacuación total no se sigue.',
};

// Whether the panel's PANEL OK will still come on its own and, when it won't,
// why: from its brand, its connection and what the automatic tracking (rebuilt
// from the stored events on connect and at startup) is waiting for.
function autoOutlook(p) {
  const offline = p.status && p.status !== 'connected';
  const offlineNote = offline ? ' El panel está desconectado.' : '';
  if (!RECOVERY_RULES[p.brand]) {
    return { tone: 'stuck', label: 'Sin detección', detail: 'Esta marca no tiene detección automática de PANEL OK.' };
  }
  if ((p.brand === 'notifier' || p.brand === 'simplex') && !p.status && !p.virtual) {
    return { tone: 'stuck', label: 'Sin TCP',
      detail: 'Este panel llega por otro servicio (como mongo-notifier), no por TCP, así que el servidor no lee su respuesta de normalidad: su PANEL OK no llegará solo.' };
  }
  if (p.brand === 'notifier') {
    return { tone: 'wait', label: 'Depende del panel', detail: 'Llegará cuando el panel envíe SISTEMA NORMAL.' + offlineNote };
  }
  if (p.brand === 'simplex') {
    return { tone: 'wait', label: 'Requiere consulta', detail: 'Llegará si un operador consulta ALARMAS EN CURSO y el panel responde todo en 0.' + offlineNote };
  }
  const t = p.tracker;
  if (t && t.rebuild_error) {
    return { tone: 'stuck', label: 'Falló la reconstrucción',
      detail: `La última reconstrucción del seguimiento${t.rebuilt_at ? ` (${formatTs(t.rebuilt_at, { full:true })})` : ''} falló: ${t.rebuild_error}. `
        + 'Lo que espera puede no incluir los eventos anteriores. Usa «Reconstruir» para reintentar.' };
  }
  if (t && t.brand_changed) {
    return { tone: 'stuck', label: 'Cambió la marca',
      detail: `El seguimiento se reconstruyó como ${BRAND_LABELS[t.rebuilt_brand] || t.rebuilt_brand} y el panel ahora es ${BRAND_LABELS[p.brand] || p.brand}. `
        + 'Usa «Reconstruir». Los eventos guardados con la marca anterior no se emparejan: resuélvelos con un PANEL OK manual.' };
  }
  if (t && t.pending.length > 0) {
    const shown = t.pending.slice(0, 8).join(', ') + (t.pending.length > 8 ? ` y ${t.pending.length - 8} más` : '');
    return { tone: 'wait', label: `Espera ${t.pending.length} ${t.pending.length === 1 ? 'restauración' : 'restauraciones'}`,
      detail: `Llegará cuando se restauren: ${shown}.` + offlineNote };
  }
  if (!t || !t.tracked) {
    return offline
      ? { tone: 'idle', label: 'Desconectado',
          detail: 'El seguimiento se reconstruye desde los eventos guardados cuando el panel se reconecte.' }
      : { tone: 'stuck', label: 'Sin seguimiento',
          detail: 'El servidor no tiene el seguimiento de este panel: no se ha reconstruido desde que arrancó el servicio, o se perdió al cortarse la conexión. Usa «Reconstruir».' };
  }
  // Nothing pending in memory: compare with what the stored events say.
  const pr = p.pairing;
  if (pr && pr.open.total > 0) {
    return { tone: 'stuck', label: 'Seguimiento desfasado',
      detail: `Los eventos guardados tienen ${pr.open.total} sin restaurar, pero el seguimiento en memoria no espera ninguno. Usa «Reconstruir».` };
  }
  if (pr && pr.restored > 0 && pr.untracked === 0) {
    return { tone: 'stuck', label: 'Todo restaurado',
      detail: 'Todos sus eventos ya recibieron su restauración, pero el PANEL OK no salió. Usa «Reconstruir»: emite el PANEL OK que faltaba.' };
  }
  return { tone: 'stuck', label: 'Eventos no seguidos',
    detail: 'El seguimiento no espera la restauración de ninguno de estos eventos: son de un tipo que esta marca no empareja (como las supervisiones en Vigilant), no traen los datos para emparejarlos, o llegaron después de que todo se había restaurado. '
      + 'Solo un ciclo nuevo de activación y restauración emitirá PANEL OK' + (p.brand === 'hochiki' ? ', o un RESET del panel.' : '.') + offlineNote };
}

const OUTLOOK_COLORS = { wait: BC.supervisory, stuck: BC.fire, idle: { fg: BC.inkSubtle, bg: BC.bgAlt } };

// "1 alarma, 127 fallas": the non-zero counts.
function countsText(p) {
  return [[p.alarm, 'alarma', 'alarmas'], [p.trouble, 'falla', 'fallas'], [p.supervisory, 'supervisión', 'supervisiones']]
    .filter(([n]) => n > 0).map(([n, one, many]) => `${n} ${n === 1 ? one : many}`).join(', ');
}

// What emitting the PANEL OK by hand does, for this panel.
function forceEffects(p) {
  const out = [
    `Se borran las notificaciones de este panel (${countsText(p.pairing ? p.pairing.active : p) || 'ninguna activa'}) para todos los usuarios.`,
    (p.total === 1 ? 'El evento queda como resuelto.' : `Los ${p.total} eventos quedan como resueltos.`)
      + (p.pairing && p.pairing.restored > 0
          ? ` De ellos, ${p.pairing.restored} ya tenían su restauración y ${p.pairing.open.total} siguen activos según el seguimiento.` : '')
      + ' No se borra ninguno: siguen en la base de datos y en las exportaciones.'
      + (p.older_than_week > 0 ? ` ${p.older_than_week} son de hace más de una semana y dejarán de mostrarse en el dashboard.` : ''),
    'En el registro de eventos aparece un PANEL OK con el motivo que escribas. Quién lo emitió queda en Auditoría.',
  ];
  if (p.tracker) {
    const n = p.tracker.pending.length;
    out.push(n > 0
      ? `Se reinicia el seguimiento automático: se ${n === 1 ? 'olvida 1 restauración pendiente' : `olvidan ${n} restauraciones pendientes`}. Si llegan después, no emitirán otro PANEL OK.`
      : 'Se reinicia el seguimiento automático: el próximo PANEL OK automático solo considera eventos nuevos.');
  }
  if (p.brand === 'simplex') {
    out.push('Una consulta ALARMAS EN CURSO en 0 no emitirá otro PANEL OK hasta que llegue un evento nuevo.');
  }
  out.push(p.whatsapp
    ? 'WhatsApp: se reinicia la espera entre mensajes de este panel, así que su próxima alerta sale de inmediato. El PANEL OK no envía WhatsApp ni notificaciones push.'
    : 'El PANEL OK no envía WhatsApp ni notificaciones push.');
  return out;
}

function CountChips({ p }) {
  const chips = [
    { n: p.alarm, label: 'ALARMA', c: BC.fire },
    { n: p.trouble, label: 'FALLA', c: BC.trouble },
    { n: p.supervisory, label: 'SUPERV.', c: BC.supervisory },
  ].filter(x => x.n > 0);
  return (
    <span style={{ display:'inline-flex', gap:4, flexWrap:'wrap' }}>
      {chips.map(x => (
        <span key={x.label} style={{ fontFamily:BC.mono, fontSize:10, fontWeight:700, padding:'2px 6px', borderRadius:4, background:x.c.bg, color:x.c.fg, whiteSpace:'nowrap' }}>
          {x.n} {x.label}
        </span>
      ))}
    </span>
  );
}

// The unresolved column: for brands that pair each alert with its restore,
// the active ones (what the badges count: not restored), then how many
// already have their restore.
function OpenSummary({ p }) {
  const pr = p.pairing;
  if (!pr) return <CountChips p={p}/>;
  return (
    <div>
      {pr.active.total > 0
        ? <span style={{ display:'inline-flex', alignItems:'center', gap:4, flexWrap:'wrap' }}>
            <CountChips p={pr.active}/>
            <span style={{ fontSize:11, color:BC.inkMuted }}>activo{pr.active.total === 1 ? '' : 's'}</span>
          </span>
        : <span style={{ fontSize:11, fontWeight:600, color:BC.ok.fg }}>Ninguno activo</span>}
      <div style={{ fontSize:10, color:BC.inkSubtle, marginTop:3, whiteSpace:'nowrap' }}>
        de {p.total}
        {pr.restored > 0 && ` · ${pr.restored} restaurado${pr.restored === 1 ? '' : 's'}`}
        {pr.untracked > 0 && ` · ${pr.untracked} activo${pr.untracked === 1 ? '' : 's'} sin par`}
      </div>
    </div>
  );
}

// Each active event's pairing in the dialog: awaiting its restore, or not
// paired by its brand's tracking. Restored ones aren't listed.
const EVENT_STATES = {
  open:      { label: 'ACTIVO',  group: 'Esperan su restauración', c: BC.fire },
  untracked: { label: 'SIN PAR', group: 'Sin emparejar: el seguimiento no espera su restauración', c: { fg: BC.inkSubtle, bg: BC.bgAlt } },
};

function ForcePanelOkDialog({ panel, onClose, onDone }) {
  const [events, setEvents] = React.useState(null); // the /events response
  const [reason, setReason] = React.useState('');
  const [understood, setUnderstood] = React.useState(false);
  const [status, setStatus] = React.useState(null); // null | 'loading' | 'error'
  const [err, setErr] = React.useState('');
  const cid = encodeURIComponent(panel.client_id);

  // Reloaded whenever the panel gets a newer event, so the list is what is sent.
  React.useEffect(() => {
    let cancelled = false;
    const empty = { paired: false, total: 0, active: 0, events: [] };
    fetch(`/unresolved/${cid}/events?limit=300`)
      .then(r => r.ok ? r.json() : empty)
      .then(d => { if (!cancelled) setEvents(d); })
      .catch(() => { if (!cancelled) setEvents(empty); });
    return () => { cancelled = true; };
  }, [panel.client_id, panel.last_event_id]);

  async function submit() {
    setStatus('loading'); setErr('');
    try {
      const res = await fetch(`/unresolved/${cid}/panel-ok`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim(), last_event_id: panel.last_event_id }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setStatus('error');
        setErr(typeof data.detail === 'string' ? data.detail : 'No se pudo emitir el PANEL OK.');
        if (res.status === 409) onDone(false); // reload: the panel changed
        return;
      }
      onDone(true);
    } catch (_) {
      setStatus('error'); setErr('Error de conexión.');
    }
  }

  const outlook = autoOutlook(panel);
  const oc = OUTLOOK_COLORS[outlook.tone];
  // What the badges count: the active ones when the brand pairs restores.
  const active = panel.pairing ? panel.pairing.active : panel;
  const valid = reason.trim().length >= 5 && understood;
  const sectionTitle = { fontSize:11, fontWeight:700, color:BC.inkSubtle, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:500, background:'rgba(15,23,42,.45)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
      onClick={onClose}>
      <div className="bc-slide-up" onClick={e => e.stopPropagation()}
        style={{ width:'100%', maxWidth:720, maxHeight:'calc(100vh - 32px)', display:'flex', flexDirection:'column', background:BC.surface, borderRadius:10, boxShadow:BC.shadowLg, color:BC.ink }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid '+BC.border, display:'flex', alignItems:'flex-start', gap:10 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:15, fontWeight:600 }}>Emitir PANEL OK manual</div>
            <div style={{ fontSize:12, color:BC.inkSubtle, marginTop:2 }}>{panel.description || panel.client_id}</div>
          </div>
          <button className="bc-btn" onClick={onClose} title="Cerrar"
            style={{ padding:4, border:'none', background:'transparent', color:BC.inkSubtle, display:'flex' }}>
            <Icon name="close" size={16}/>
          </button>
        </div>

        <div className="bc-scroll" style={{ overflowY:'auto', padding:'14px 18px', display:'flex', flexDirection:'column', gap:16 }}>
          {active.alarm > 0 && (
            <div style={{ padding:'10px 12px', background:BC.fire.bg, color:BC.fire.fg, border:'1px solid '+BC.fire.bar, borderRadius:6, fontSize:12, display:'flex', gap:8, alignItems:'flex-start' }}>
              <Icon name="fire" size={15} style={{ flex:'0 0 15px', marginTop:1 }}/>
              <div>Hay <b>{active.alarm} alarma{active.alarm === 1 ? '' : 's'} de incendio</b> activa{active.alarm === 1 ? '' : 's'}. Confirma en sitio que no hay un incendio en curso antes de continuar.</div>
            </div>
          )}

          <div>
            <div style={sectionTitle}>Eventos que quedarán resueltos <CountChips p={active}/></div>
            {events && events.paired && (
              <div style={{ fontSize:12, color:BC.inkMuted, marginBottom:6 }}>
                <b style={{ color: events.active ? BC.fire.fg : BC.ok.fg }}>{events.active} siguen activos</b>
                {events.restored > 0 && ` · ${events.restored} ya restaurados (no se muestran)`}
              </div>
            )}
            <div style={{ border:'1px solid '+BC.border, borderRadius:6, maxHeight:260, overflowY:'auto' }} className="bc-scroll">
              {events === null && <div style={{ padding:16, textAlign:'center', color:BC.inkFaint, fontSize:12 }}>Cargando…</div>}
              {events && events.events.map((ev, i) => {
                const c = classifyColor(ev.cls);
                const st = EVENT_STATES[ev.state];
                const prev = i > 0 ? events.events[i - 1].state : undefined;
                return (
                  <React.Fragment key={ev.id}>
                    {st && ev.state !== prev && (
                      <div style={{ position:'sticky', top:0, zIndex:1, padding:'5px 10px', background:BC.surfaceAlt, borderBottom:'1px solid '+BC.border, fontSize:10, fontWeight:700, color:BC.inkSubtle, textTransform:'uppercase', letterSpacing:'.06em' }}>
                        {st.group} ({events[ev.state]})
                      </div>
                    )}
                    <div style={{ display:'flex', flexWrap:'wrap', columnGap:10, rowGap:2, alignItems:'baseline', padding:'6px 10px', borderBottom:'1px solid '+BC.divider, fontSize:12 }}>
                      <span style={{ fontFamily:BC.mono, fontSize:10, fontWeight:700, color:c.fg, flex:'0 0 78px' }}>{classifyLabel(ev.cls)}</span>
                      <span style={{ fontFamily:BC.mono, fontSize:11, color:BC.inkSubtle, flex:'0 0 auto', whiteSpace:'nowrap' }}>{formatTs(ev.at)}</span>
                      {/* on a phone it takes its own line */}
                      <span style={{ flex:'1 1 220px', minWidth:0, overflowWrap:'anywhere' }}>{ev.description}</span>
                      {st && (
                        <span style={{ fontFamily:BC.mono, fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:4, background:st.c.bg, color:st.c.fg, whiteSpace:'nowrap' }}>{st.label}</span>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
              {events && events.events.length === 0 && (
                <div style={{ padding:16, textAlign:'center', color:BC.inkFaint, fontSize:12 }}>Ningún evento activo: todos ya se restauraron.</div>
              )}
              {events && events.active > events.events.length && (
                <div style={{ padding:'8px 10px', fontSize:11, color:BC.inkSubtle, textAlign:'center' }}>
                  y {events.active - events.events.length} más
                </div>
              )}
            </div>
          </div>

          <div>
            <div style={sectionTitle}>Cómo se recupera este panel</div>
            <div style={{ fontSize:12, color:BC.inkMuted, marginBottom:8 }}>
              {RECOVERY_RULES[panel.brand] || 'Sin regla automática para esta marca.'}
              {panel.tracker && panel.tracker.rebuilt_at && ` Seguimiento reconstruido desde los eventos guardados el ${formatTs(panel.tracker.rebuilt_at, { full:true })}.`}
            </div>
            <div style={{ padding:'8px 12px', background:oc.bg, color:oc.fg, borderRadius:6, fontSize:12 }}>
              <b>{outlook.label}.</b> {outlook.detail}
            </div>
          </div>

          <div>
            <div style={sectionTitle}>Qué pasará</div>
            <ul style={{ margin:0, paddingLeft:18, fontSize:12, color:BC.inkMuted, display:'flex', flexDirection:'column', gap:4 }}>
              {forceEffects(panel).map((t, i) => <li key={i}>{t}</li>)}
            </ul>
            <div style={{ marginTop:10, padding:'8px 12px', background:BC.pending.bg, color:BC.pending.fg, borderRadius:6, fontSize:12, display:'flex', gap:8, alignItems:'flex-start' }}>
              <Icon name="warning" size={14} style={{ flex:'0 0 14px', marginTop:1 }}/>
              <div>No se envía ningún comando al panel. Si la condición sigue activa en el panel, BControl no lo sabrá hasta que el panel envíe un evento nuevo. No se puede deshacer.</div>
            </div>
          </div>

          <div>
            <label style={{ ...sectionTitle, display:'block' }}>Motivo (obligatorio, es el texto que verán los operadores)</label>
            <textarea className="bc-input" value={reason} onChange={e => setReason(e.target.value)} maxLength={300} rows={2}
              placeholder="Ej.: falla reparada en sitio, el panel no envió la restauración"
              style={{ width:'100%', padding:'8px 10px', border:'1px solid '+BC.border, borderRadius:5, background:BC.surfaceAlt, fontSize:13, color:BC.ink, resize:'vertical' }}/>
            <label style={{ display:'flex', gap:8, alignItems:'flex-start', fontSize:12, color:BC.ink, marginTop:8, cursor:'pointer' }}>
              <input type="checkbox" checked={understood} onChange={e => setUnderstood(e.target.checked)}
                style={{ width:16, height:16, marginTop:0, accentColor:BC.brand, flex:'0 0 16px' }}/>
              Revisé el estado del panel y entiendo que esto solo marca estos eventos como resueltos en BControl.
            </label>
          </div>
        </div>

        {/* Outside the scrolling body, so a refusal shows next to the button. */}
        {status === 'error' && (
          <div style={{ margin:'0 18px 10px', padding:'8px 12px', background:BC.fire.bg, color:BC.fire.fg, border:'1px solid '+BC.fire.bar, borderRadius:5, fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
            <Icon name="warning" size={13}/> {err}
          </div>
        )}
        <div style={{ padding:'12px 18px', borderTop:'1px solid '+BC.border, display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button className="bc-btn" onClick={onClose}
            style={{ padding:'8px 16px', border:'1px solid '+BC.border, background:BC.surface, color:BC.ink, borderRadius:5, fontSize:13, fontWeight:600 }}>
            Cancelar
          </button>
          <button className="bc-btn" onClick={submit} disabled={!valid || status === 'loading'}
            style={{ padding:'8px 16px', border:'none', background:BC.ok.bar, color:'#fff', borderRadius:5, fontSize:13, fontWeight:600 }}>
            {status === 'loading' ? '…' : 'Emitir PANEL OK'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UnresolvedTab({ panels, loading, reload }) {
  const [selected, setSelected] = React.useState(null); // the panel's row when opened
  const [history, setHistory] = React.useState([]);
  const [notice, setNotice] = React.useState(null); // { text, error }
  const [rebuilding, setRebuilding] = React.useState(null); // client_id

  async function loadHistory() {
    try {
      const res = await fetch('/unresolved/history?limit=50');
      if (res.ok) setHistory(await res.json());
    } catch (_) {}
  }
  React.useEffect(() => { loadHistory(); }, []);

  function flash(text, error = false) {
    setNotice({ text, error });
    setTimeout(() => setNotice(null), error ? 10000 : 6000);
  }

  // Rebuild the panel's tracking from its stored events now.
  async function rebuild(p) {
    const name = p.description || p.client_id;
    setRebuilding(p.client_id);
    try {
      const res = await fetch(`/unresolved/${encodeURIComponent(p.client_id)}/rebuild`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const n = data.tracker.pending.length;
        flash(`Seguimiento de ${name} reconstruido: ${data.replayed} eventos repasados, ${n} ${n === 1 ? 'punto pendiente' : 'puntos pendientes'}.`
          + (data.emitted ? ' Todo estaba restaurado: se emitió el PANEL OK que faltaba.' : ''));
      } else {
        flash(typeof data.detail === 'string' ? data.detail : `No se pudo reconstruir el seguimiento de ${name}.`, true);
      }
    } catch (_) {
      flash('Error de conexión.', true);
    }
    setRebuilding(null);
    reload(); loadHistory();
  }

  // The dialog follows the panel's latest row, so the counts and the event it
  // confirms up to stay current while it is open.
  const panel = selected && panels.find(p => p.client_id === selected.client_id);

  // The panel left the list while its dialog was open: its own PANEL OK came,
  // or another admin emitted one.
  React.useEffect(() => {
    if (selected && !panel) {
      setSelected(null);
      flash(`${selected.description || selected.client_id} ya no tiene eventos sin resolver.`);
      loadHistory();
    }
  }, [selected, panel]);

  function done(ok) {
    reload(); loadHistory();
    if (ok) {
      setSelected(null);
      flash(`PANEL OK emitido para ${panel.description || panel.client_id}.`);
    }
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
      <div style={{ background:BC.surface, border:'1px solid '+BC.border, borderRadius:8, boxShadow:BC.shadow }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid '+BC.border, display:'flex', alignItems:'flex-start', gap:10 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:14, fontWeight:600 }}>Eventos sin resolver</div>
            <div style={{ fontSize:11, color:BC.inkSubtle, marginTop:2 }}>
              Alarmas, fallas y supervisiones sin un PANEL OK posterior: son las que mantienen las notificaciones.
              Si un panel no va a emitir su PANEL OK por sí solo, puedes emitirlo tú.
            </div>
          </div>
          <button className="bc-btn" onClick={() => { reload(); loadHistory(); }} title="Actualizar"
            style={{ padding:'5px 10px', fontSize:12, background:'transparent', border:'1px solid '+BC.border, borderRadius:4, color:BC.inkMuted, display:'flex', alignItems:'center', gap:5 }}>
            <Icon name="refresh" size={12}/> Actualizar
          </button>
        </div>
        {notice && (() => {
          const c = notice.error ? BC.fire : BC.ok;
          return (
            <div style={{ margin:'12px 18px 0', padding:'8px 12px', background:c.bg, color:c.fg, border:'1px solid '+c.bar, borderRadius:5, fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
              <Icon name={notice.error ? 'warning' : 'check'} size={13}/> {notice.text}
            </div>
          );
        })()}
        {loading ? (
          <div style={{ padding:'40px', textAlign:'center', color:BC.inkFaint, fontSize:12 }}>Cargando…</div>
        ) : (
          <div className="bc-admin-table-wrap"><table className="bc-tbl" style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><th>Panel</th><th title="Activos: esperan su restauración. Restaurados: ya la recibieron, solo falta el PANEL OK. Sin par: el seguimiento de su marca no los empareja.">Sin resolver</th><th>Desde</th><th>Último</th><th title="Si el panel emitirá su PANEL OK por sí solo">PANEL OK automático</th><th></th></tr></thead>
            <tbody>
              {panels.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign:'center', padding:'24px', color:BC.inkFaint }}>Ningún panel tiene eventos sin resolver.</td></tr>
              )}
              {panels.map(p => {
                const o = autoOutlook(p);
                const oc = OUTLOOK_COLORS[o.tone];
                return (
                  <tr key={p.client_id}>
                    <td style={{ minWidth:160, fontWeight:500, fontSize:13 }}>{p.description || p.client_id}</td>
                    <td><OpenSummary p={p}/></td>
                    <td style={{ fontSize:12, color:BC.inkSubtle, whiteSpace:'nowrap' }} title={formatTs(p.oldest, { full:true })}>{relTime(p.oldest)}</td>
                    <td style={{ fontSize:12, color:BC.inkSubtle, whiteSpace:'nowrap' }} title={formatTs(p.newest, { full:true })}>{relTime(p.newest)}</td>
                    <td title={o.detail}>
                      <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:4, background:oc.bg, color:oc.fg, whiteSpace:'nowrap' }}>{o.label}</span>
                    </td>
                    <td>
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'stretch', gap:4 }}>
                        <button className="bc-btn" onClick={() => setSelected(p)}
                          style={{ padding:'3px 10px', fontSize:11, background:'transparent', color:BC.ok.fg, border:'1px solid '+BC.ok.bar, borderRadius:4, whiteSpace:'nowrap' }}>
                          Emitir PANEL OK…
                        </button>
                        {p.tracker && (
                          <button className="bc-btn" onClick={() => rebuild(p)} disabled={rebuilding !== null}
                            title="Vuelve a armar el seguimiento desde los eventos guardados, sin esperar a que el panel se reconecte. Si todo ya estaba restaurado, emite el PANEL OK que faltaba."
                            style={{ padding:'3px 10px', fontSize:11, background:'transparent', color:BC.inkMuted, border:'1px solid '+BC.border, borderRadius:4, whiteSpace:'nowrap', display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                            <Icon name="refresh" size={11}/> {rebuilding === p.client_id ? 'Reconstruyendo…' : 'Reconstruir'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>

      <div style={{ background:BC.surface, border:'1px solid '+BC.border, borderRadius:8, boxShadow:BC.shadow }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid '+BC.border }}>
          <div style={{ fontSize:14, fontWeight:600 }}>PANEL OK manuales y reconstrucciones</div>
          <div style={{ fontSize:11, color:BC.inkSubtle, marginTop:2 }}>Quién los hizo, sobre qué eventos y por qué, incluidos los intentos rechazados o fallidos.</div>
        </div>
        <div className="bc-admin-table-wrap"><table className="bc-tbl" style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr><th>Fecha</th><th>Acción</th><th>Usuario</th><th>Panel</th><th>Detalle</th><th>Resultado</th></tr></thead>
          <tbody>
            {history.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign:'center', padding:'24px', color:BC.inkFaint }}>Aún no hay PANEL OK manuales ni reconstrucciones.</td></tr>
            )}
            {history.map(h => (
              <tr key={h.id}>
                <td style={{ fontFamily:BC.mono, fontSize:12, color:BC.inkSubtle, whiteSpace:'nowrap' }}>{formatTs(h.at, { full:true })}</td>
                <td style={{ fontSize:12, whiteSpace:'nowrap' }}>{h.action === 'PANEL_OK_TRACKING_REBUILT' ? 'Reconstrucción' : 'PANEL OK manual'}</td>
                <td>{h.user}</td>
                <td style={{ fontSize:12, color:BC.inkMuted }}>{h.client_description || h.client_id || '—'}</td>
                <td style={{ fontSize:12 }}>{h.message}{!h.success && h.reason && <div style={{ color:BC.fire.fg, marginTop:2 }}>{h.reason}</div>}</td>
                <td>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11, color: h.success ? BC.ok.fg : BC.fire.fg }}>
                    <Icon name={h.success ? 'check' : 'warning'} size={12}/>
                    {h.action === 'PANEL_OK_TRACKING_REBUILT'
                      ? (h.success ? 'Reconstruido' : 'Falló')
                      : (h.success ? 'Emitido' : 'Rechazado')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {panel && <ForcePanelOkDialog panel={panel} onClose={() => setSelected(null)} onDone={done}/>}
    </div>
  );
}

function AdminView({ user }) {
  const [tab, setTab] = React.useState('audit');
  const [audit, setAudit] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [unresolved, setUnresolved] = React.useState([]);
  const [unresolvedLoading, setUnresolvedLoading] = React.useState(true);

  async function loadUnresolved() {
    try {
      const res = await fetch('/unresolved');
      if (res.ok) setUnresolved(await res.json());
    } catch (_) {}
    setUnresolvedLoading(false);
  }

  React.useEffect(() => {
    if (user.role !== 'admin') return;
    loadUnresolved();
    const t = setInterval(loadUnresolved, 15000);
    return () => clearInterval(t);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    async function fetchAudit() {
      try {
        const res = await fetch('/audit?limit=100');
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setAudit(data);
      } catch (_) {}
      if (!cancelled) setLoading(false);
    }
    fetchAudit();
    const t = setInterval(fetchAudit, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const tabs = [
    { id: 'audit', label: 'Auditoría', icon: 'terminal', count: audit.length },
    ...(user.role === 'admin' ? [
      { id: 'unresolved', label: 'Sin resolver', icon: 'bell', count: unresolved.length },
      { id: 'users', label: 'Usuarios', icon: 'user', count: null },
    ] : []),
  ];

  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', background:BC.bg, minHeight:0 }}>
      <div style={{ padding:'18px 22px 0', background:BC.surface, borderBottom:'1px solid '+BC.border }}>
        <div style={{ fontSize:18, fontWeight:600, marginBottom:4, display:'flex', alignItems:'center', gap:10 }}>
          <Icon name="shield-admin" size={20} color={BC.brand}/>
          Administración
          <RoleBadge role={user.role}/>
        </div>
        <div style={{ fontSize:12, color:BC.inkSubtle, marginBottom:14 }}>Registro de auditoría de acciones de usuarios y comandos enviados a paneles.</div>
        <div style={{ display:'flex', gap:0, borderBottom:'none' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={'bc-btn bc-tab ' + (tab===t.id ? 'active' : '')}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 18px', border:'none', background:'transparent', color: tab===t.id ? BC.brand : BC.inkMuted, fontSize:13, fontWeight: tab===t.id ? 600 : 500, borderBottom:'2px solid '+(tab===t.id ? BC.brand : 'transparent'), marginBottom:-1, fontFamily:'inherit' }}>
              <Icon name={t.icon} size={14}/> {t.label}
              {t.count !== null && <span style={{ fontFamily:BC.mono, fontSize:10, padding:'1px 6px', borderRadius:4, background:tab===t.id ? BC.brandTint : BC.bgAlt, color:tab===t.id ? BC.brand : BC.inkSubtle }}>{t.count}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="bc-scroll" style={{ flex:1, overflowY:'auto', padding:22 }}>
        {tab === 'users' && <UsersTab user={user}/>}
        {tab === 'unresolved' && <UnresolvedTab panels={unresolved} loading={unresolvedLoading} reload={loadUnresolved}/>}
        {tab === 'audit' && (
          <div style={{ background:BC.surface, border:'1px solid '+BC.border, borderRadius:8, boxShadow:BC.shadow }}>
            <div style={{ padding:'14px 18px', borderBottom:'1px solid '+BC.border }}>
              <div style={{ fontSize:14, fontWeight:600 }}>Registro de auditoría</div>
              <div style={{ fontSize:11, color:BC.inkSubtle, marginTop:2 }}>Acciones de usuarios y comandos enviados a paneles.</div>
            </div>
            {loading ? (
              <div style={{ padding:'40px 12px', textAlign:'center', color:BC.inkFaint, fontSize:12 }}>Cargando…</div>
            ) : (
              <div className="bc-admin-table-wrap"><table className="bc-tbl" style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr><th>Fecha</th><th>Acción</th><th>Usuario</th><th>Panel</th><th>Mensaje</th><th>Resultado</th></tr></thead>
                <tbody>
                  {audit.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign:'center', padding:'24px', color:BC.inkFaint }}>Sin registros de auditoría.</td></tr>
                  )}
                  {audit.map(a => {
                    const isError = !a.success;
                    return (
                      <tr key={a.id}>
                        <td style={{ fontFamily:BC.mono, fontSize:12, color:BC.inkSubtle }}>{formatTs(a.at, { full:true })}</td>
                        <td><span style={{ fontFamily:BC.mono, fontSize:11, padding:'2px 8px', background: isError ? BC.fire.bg : BC.brandTint, color: isError ? BC.fire.fg : BC.brandDeep, borderRadius:4 }}>{a.action}</span></td>
                        <td>{a.user}</td>
                        <td style={{ fontSize:12, color:BC.inkMuted }}>{a.client_description || '—'}</td>
                        <td style={{ fontFamily:BC.mono, fontSize:12, color:BC.ink }}>{a.message || '—'}</td>
                        <td>
                          <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11, color: isError ? BC.fire.fg : BC.ok.fg }}>
                            <Icon name={isError ? 'warning' : 'check'} size={12}/>
                            {isError ? 'Error' : 'OK'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

window.AdminView = AdminView;
